/**
 * EMT job-preference filtering (Item 2 backlog). Pure functions so they're
 * independently testable, mirroring the pattern in certificationMatching.ts.
 *
 * Fail-open policy: when a job is missing the data needed to evaluate a given
 * preference (no geocoded location, no pay listed, no jobTypes/schedule tag),
 * that dimension does NOT exclude the job. Excluding on missing data would
 * silently hide jobs rather than genuinely filter them, and most job docs in
 * this codebase have partial/optional fields (see jobFormModel.ts — pay,
 * schedule, jobTypes, jobLocation are all optional). Only jobs that actively
 * declare data conflicting with a set preference are excluded.
 */
import { haversineMiles, isValidLatLng } from '../profileCoordinates';
import type { Job } from '../firestore/types';
import type { EMTJobPreferences } from '../../types/firestore';

export type LatLng = { lat: number; lng: number };

export function getJobCoordinates(job: Job): LatLng | null {
  const jl = job.jobLocation;
  if (jl && isValidLatLng(jl.lat, jl.lng)) return { lat: jl.lat as number, lng: jl.lng as number };
  const geo = job.geo;
  if (geo && isValidLatLng(geo.lat, geo.lng)) return { lat: geo.lat, lng: geo.lng };
  return null;
}

function jobLocationText(job: Job): string {
  const jl = job.jobLocation;
  if (jl) {
    const parts = [jl.city, jl.stateCode].filter(Boolean);
    if (parts.length) return parts.join(', ').toLowerCase();
    if (jl.formattedAddress) return jl.formattedAddress.toLowerCase();
  }
  if (typeof job.location === 'string') return job.location.toLowerCase();
  if (typeof job.locationText === 'string') return job.locationText.toLowerCase();
  return '';
}

export function jobMatchesShiftPreference(job: Job, prefs: EMTJobPreferences): boolean {
  if (!prefs.shiftTypes?.length) return true;
  if (!job.schedule) return true; // fail-open: unset schedule
  return (prefs.shiftTypes as string[]).includes(job.schedule);
}

export function jobMatchesEmploymentTypePreference(job: Job, prefs: EMTJobPreferences): boolean {
  if (!prefs.employmentTypes?.length) return true;
  const jobTypes = job.jobTypes ?? [];
  if (jobTypes.length === 0) return true; // fail-open: untagged job
  return (prefs.employmentTypes as string[]).some((t) => jobTypes.includes(t));
}

export function jobMatchesRoleTypePreference(job: Job, prefs: EMTJobPreferences): boolean {
  if (!prefs.roleTypes?.length) return true;
  const preferredExperienceTypes = job.preferredExperienceTypes ?? [];
  const isEmergencyDeployment = (job.jobTypes ?? []).includes('emergency_deployment');
  // preferredExperienceTypes is an optional employer hint (rarely set) — a job
  // that declares neither this nor the emergency flag isn't excluded, since we
  // can't know it's a mismatch, only that it wasn't tagged.
  if (preferredExperienceTypes.length === 0 && !isEmergencyDeployment) return true;
  return prefs.roleTypes.some((rt) =>
    rt === 'Emergency Deployment' ? isEmergencyDeployment : preferredExperienceTypes.includes(rt),
  );
}

/**
 * The preferences without pay: what may be copied to the public profile card. Pay stays private (the EMT
 * was never told employers would see it); job alerts read it from the private profile on the server.
 */
export function withoutPay(prefs: EMTJobPreferences): EMTJobPreferences {
  const out = { ...prefs };
  delete out.payMin;
  delete out.payMax;
  return out;
}

export function jobMatchesPayPreference(job: Job, prefs: EMTJobPreferences): boolean {
  if (prefs.payMin == null && prefs.payMax == null) return true;
  if (job.minPay == null && job.maxPay == null) return true; // fail-open: no pay listed
  const prefMin = prefs.payMin ?? -Infinity;
  const prefMax = prefs.payMax ?? Infinity;
  const jobMin = job.minPay ?? job.maxPay ?? -Infinity;
  const jobMax = job.maxPay ?? job.minPay ?? Infinity;
  return jobMin <= prefMax && jobMax >= prefMin;
}

export function jobMatchesLocationPreference(
  job: Job,
  prefs: EMTJobPreferences,
  emtCoords: LatLng | null,
): boolean {
  const radiusActive = prefs.maxTravelRadiusMiles != null && emtCoords != null;
  const preferredLocationsActive = Boolean(prefs.preferredLocations?.length);
  if (!radiusActive && !preferredLocationsActive) return true;

  let radiusPass = false;
  if (radiusActive) {
    const jobCoords = getJobCoordinates(job);
    radiusPass = jobCoords
      ? haversineMiles(emtCoords!.lat, emtCoords!.lng, jobCoords.lat, jobCoords.lng) <=
        prefs.maxTravelRadiusMiles!
      : true; // fail-open: job not geocoded
  }

  let locationPass = false;
  if (preferredLocationsActive) {
    const text = jobLocationText(job);
    locationPass = text
      ? prefs.preferredLocations!.some((loc) => text.includes(loc.trim().toLowerCase()))
      : true; // fail-open: job has no location text at all
  }

  // Both set: a job matching either counts (radius is a lower-friction default,
  // preferred locations lets an EMT add specific cities outside that radius).
  if (radiusActive && preferredLocationsActive) return radiusPass || locationPass;
  return radiusActive ? radiusPass : locationPass;
}

export function jobMatchesEmtPreferences(
  job: Job,
  prefs: EMTJobPreferences | undefined | null,
  emtCoords: LatLng | null,
): boolean {
  if (!prefs) return true;
  return (
    jobMatchesShiftPreference(job, prefs) &&
    jobMatchesEmploymentTypePreference(job, prefs) &&
    jobMatchesRoleTypePreference(job, prefs) &&
    jobMatchesPayPreference(job, prefs) &&
    jobMatchesLocationPreference(job, prefs, emtCoords)
  );
}
